/**
 * ChatContext — globaler Open/Close-State für das Premium-Support-Chat-Panel
 *
 * Architektur-Entscheidung (gegen URL-basiertes Routing):
 *   Der Chat ist immer ein Overlay über der aktuellen Seite — nie eine eigene
 *   Route. User klickt Bubble → Panel öffnet als Mini-Panel (Desktop) oder
 *   Bottom-Sheet (Mobile/Tablet). User schließt Panel → ist exakt wieder auf
 *   derselben Inhaltsseite, am selben Scroll-Stand. Kein Routenwechsel,
 *   kein Verlust von Page-State.
 *
 * Vorteile gegenüber URL-basiertem Ansatz:
 *   - User-Workflow nicht unterbrochen (keine History-Manipulation)
 *   - Keine Page-Reloads, keine Lazy-Chunk-Loads zwischen Seiten
 *   - Sauberer Service-Worker-Push-Deep-Link: Notification öffnet z.B.
 *     /dashboard?openChat=1 → triggert openPanel() auf der "echten" Seite
 *   - Keine Routing-Komplexität für nicht-berechtigte User (gibt's hier
 *     gar nicht — Bubble ist für sie unsichtbar)
 *
 * Pattern: extrem schlanker State-Container. Komplexere Chat-Daten
 * (Konversationen, Nachrichten, Verfügbarkeit) bekommen später eigene
 * Hooks/Services und werden NICHT in diesen Context gepackt — Trennung von
 * UI-State und Daten-State.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ChatContextValue {
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

/**
 * Push-Notification-Klick öffnet via SW eine URL wie /dashboard?openSupportChat=1.
 * Dieser Effect entdeckt den Query-Param, öffnet das Panel und entfernt den Param
 * wieder aus der URL, damit ein Reload nicht erneut auslöst.
 */
function useAutoOpenFromQuery(setOpen: (open: boolean) => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('openSupportChat') === '1') {
      setOpen(true);
      params.delete('openSupportChat');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, [setOpen]);
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  useAutoOpenFromQuery(setIsPanelOpen);

  const value = useMemo<ChatContextValue>(
    () => ({ isPanelOpen, openPanel, closePanel, togglePanel }),
    [isPanelOpen, openPanel, closePanel, togglePanel]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

/**
 * Lese den Chat-UI-State.
 * Muss innerhalb von <ChatProvider> aufgerufen werden.
 */
export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat() muss innerhalb eines <ChatProvider> aufgerufen werden.');
  }
  return ctx;
}
